import { useState } from 'react'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'
import { Button, Radio } from '@factorialco/deprecated-design-system'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { EmployeesEmployeeSelectorInput, TeamDetailFragment } from 'generated/resources/graphql'
import useAddMembers from 'modules/teams/components/Detail/hooks/useAddMembers'
import { handleResult } from 'modules/core/lib/handleMutation'
import app from 'modules/core/store/models/app'

interface Props {
  team: TeamDetailFragment
  onClose: () => void
  selectedGroup?: string
}

// Convert EmployeeSelectorV2MultiSelectState to backend-compatible format
const selectionToBackendFormat = (
  selection: EmployeeSelectorV2MultiSelectState
): EmployeesEmployeeSelectorInput => {
  if (selection.type === 'manual') {
    return {
      type: 'manual',
      ids: selection.ids,
    }
  }
  return {
    type: 'all',
    filters: selection.filters,
    excludedIds: selection.excludedIds ?? [],
  }
}

const MembersModal = ({ team, onClose, selectedGroup = '' }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const { addMembers, loading } = useAddMembers()
  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })
  const membershipsWithoutTerminatedEmployees = team.memberships.filter((m) => {
    const terminatedOn = m.employee.terminatedOn
    return !terminatedOn || stringToDate(terminatedOn, 'backend').isAfter(moment(), 'day')
  })
  const [group, setGroup] = useState(selectedGroup)
  const leaders = membershipsWithoutTerminatedEmployees.filter((m) => m.lead)
  const leadersToShow = leaders.filter((m) => !m.employee.terminatedOn)

  const onSubmit = async () => {
    const isMembersGroup = group === 'members'
    const isLeadersGroup = group === 'leaders'
    const isManualSelection = employeeSelection.type === 'manual'

    const selectedIds = isManualSelection ? employeeSelection.ids : []
    const selectionInput = isManualSelection
      ? undefined
      : selectionToBackendFormat(employeeSelection)

    const variables = {
      teamId: team.id,
      employeeIds: isMembersGroup && isManualSelection ? selectedIds : [],
      leadEmployeeIds: isLeadersGroup && isManualSelection ? selectedIds : [],
      employeeSelection: isMembersGroup ? selectionInput : undefined,
      leadEmployeeSelection: isLeadersGroup ? selectionInput : undefined,
    }

    const result = await addMembers(variables)

    const handled = handleResult(result, () => undefined)

    track(trackingEvents.teams.addTeamDetailsMembersSubmit, {
      is_successful: handled.success,
      error_code: !handled.success && JSON.stringify(handled.errors),
      number_of_members: selectedIds.length,
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }

    setEmployeeSelection({ type: 'manual', ids: [] })
  }

  const handleClose = () => {
    onClose()
    track(trackingEvents.teams.addTeamDetailsMembersCancel)
  }

  const handleGroupChange = (value: string) => {
    setGroup(value)
    // Reset selection when switching groups
    setEmployeeSelection({ type: 'manual', ids: [] })
  }

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        title={i18n.t('teams.members_modal.title')}
        description={i18n.t('teams.members_modal.description')}
        illustration="group"
        onClose={handleClose}
        onSubmit={onSubmit}
        footer={
          <Button submit loading={loading}>
            {i18n.t('common.submit')}
          </Button>
        }
      >
        <Radio.Wrap>
          <Radio
            name={i18n.t('teams.add_team_members')}
            value={group}
            onChange={handleGroupChange}
            description={i18n.t('teams.members.new_description')}
            option={{
              label: i18n.t('teams.add_team_members'),
              value: 'members',
            }}
          >
            <EmployeeSelectorV2
              multiple
              onChange={setEmployeeSelection}
              forcedFilters={{
                excludeIds: membershipsWithoutTerminatedEmployees.map((m) => m.employee.id),
              }}
            />
          </Radio>
        </Radio.Wrap>
        <Radio.Wrap>
          <Radio
            name={i18n.t('teams.add_team_leaders')}
            value={group}
            onChange={handleGroupChange}
            description={i18n.t('teams.leaders_modal.description')}
            option={{
              label: i18n.t('teams.add_team_leaders'),
              value: 'leaders',
            }}
          >
            <EmployeeSelectorV2
              multiple
              onChange={setEmployeeSelection}
              forcedFilters={{
                excludeIds: leadersToShow.map((l) => l.id),
              }}
            />
          </Radio>
        </Radio.Wrap>
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default MembersModal
